import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type {
  ExternalPropertiesResponse,
  ImportRunDetail,
  ImportRunMode,
  ImportRunSummary,
  ImportRunsResponse,
  IntegrationConnection,
  IntegrationConnectionsResponse,
  PropertySelection,
  UpdateIntegrationConnectionBody,
} from '@brimo/shared';
import { ApiError, api } from '../../../../lib/api';
import { queryRoots } from '../../../../lib/events';
import { isRunActive } from './report';

/**
 * Integrations of the organization (docs/dev/api-integrations.md): connections, the TeamProQ object list,
 * the property selection, import runs. Query root `['organization', 'integrations']`; while a run is queued or
 * running the connection, the run list and the run detail poll every 3 s.
 */

const BASE = '/settings/organization/integrations';
const POLL_MS = 3_000;

export const integrationKeys = {
  all: [...queryRoots.organization, 'integrations'] as const,
  connections: () => [...queryRoots.organization, 'integrations', 'connections'] as const,
  properties: (connectionId: string) =>
    [...queryRoots.organization, 'integrations', 'properties', connectionId] as const,
  runs: (connectionId: string) => [...queryRoots.organization, 'integrations', 'runs', connectionId] as const,
  run: (runId: string) => [...queryRoots.organization, 'integrations', 'run', runId] as const,
};

const noRetryOnClientError = (count: number, error: Error) =>
  !(error instanceof ApiError && error.status < 500) && count < 1;

export function useIntegrationConnections() {
  return useQuery({
    queryKey: integrationKeys.connections(),
    queryFn: ({ signal }) => api<IntegrationConnectionsResponse>(BASE, { signal }),
    retry: noRetryOnClientError,
    refetchInterval: (query) =>
      query.state.data?.items.some((item) => item.activeRunId !== null) ? POLL_MS : false,
  });
}

export function useExternalProperties(connectionId: string, enabled: boolean) {
  return useQuery({
    queryKey: integrationKeys.properties(connectionId),
    queryFn: ({ signal }) =>
      api<ExternalPropertiesResponse>(`${BASE}/teamproq/${connectionId}/properties`, { signal }),
    enabled: enabled && connectionId !== '',
    retry: noRetryOnClientError,
    staleTime: 60_000,
    // A refetch resets the checkboxes to the stored selection; only explicit invalidations reload the list.
    refetchOnWindowFocus: false,
  });
}

export function useImportRuns(connectionId: string, poll: boolean) {
  return useQuery({
    queryKey: integrationKeys.runs(connectionId),
    queryFn: ({ signal }) => api<ImportRunsResponse>(`${BASE}/teamproq/${connectionId}/runs`, { signal }),
    enabled: connectionId !== '',
    retry: noRetryOnClientError,
    refetchInterval: (query) =>
      poll || query.state.data?.items.some((run) => isRunActive(run.status)) ? POLL_MS : false,
  });
}

export function useImportRun(runId: string) {
  return useQuery({
    queryKey: integrationKeys.run(runId),
    queryFn: ({ signal }) => api<ImportRunDetail>(`${BASE}/runs/${runId}`, { signal }),
    enabled: runId !== '',
    retry: noRetryOnClientError,
    refetchInterval: (query) => (query.state.data && isRunActive(query.state.data.status) ? POLL_MS : false),
  });
}

function useInvalidateIntegrations() {
  const queryClient = useQueryClient();
  return () => queryClient.invalidateQueries({ queryKey: integrationKeys.all });
}

export function useUpdateConnection(connectionId: string) {
  const invalidate = useInvalidateIntegrations();
  return useMutation({
    mutationFn: (body: UpdateIntegrationConnectionBody) =>
      api<IntegrationConnection>(`${BASE}/teamproq/${connectionId}`, { method: 'PATCH', body }),
    onSuccess: () => void invalidate(),
  });
}

export function useSaveSelection(connectionId: string) {
  const invalidate = useInvalidateIntegrations();
  return useMutation({
    mutationFn: (body: PropertySelection) =>
      api<IntegrationConnection>(`${BASE}/teamproq/${connectionId}/selection`, { method: 'PUT', body }),
    onSuccess: () => void invalidate(),
  });
}

export function useStartRun(connectionId: string) {
  const invalidate = useInvalidateIntegrations();
  return useMutation({
    mutationFn: (mode: ImportRunMode) =>
      api<ImportRunSummary>(`${BASE}/teamproq/${connectionId}/runs`, { method: 'POST', body: { mode } }),
    onSuccess: () => void invalidate(),
  });
}
